import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { FlexContent, PublicLayout } from "../layout";
import ContentWrapper from "../layout/ContentWrapper";
import { useValidators } from "../../helpers/validation";
import { TextAreaInput, TextInput } from "../forms";
import Button from "../button/Button";
import { useCustomTranslation } from "../../helpers/translateHelpers";
import { useAuth } from "../authentication/useAuth";
import * as Sentry from "@sentry/react";
import { showToast } from "../../helpers/toastHelpers";
import SubmitButton from "../forms/SubmitButton";
import { renderPlaceholderWithAsterisk } from "../../helpers/formHelpers";
import { useState } from "react";

interface ErrorBoundaryFormProps {
  resetError: () => void;
  eventId: string;
}

interface FeedbackFormValues {
  name?: string;
  email?: string;
  message: string;
}

const ErrorBoundaryForm = ({ resetError, eventId }: ErrorBoundaryFormProps) => {
  const { tInput, tError } = useCustomTranslation();
  const validators = useValidators();

  const { user } = useAuth();

  const [sendingFeedback, setSendingFeedback] = useState(false);

  const handleGoHome = () => {
    resetError();
    window.location.href = "/";
  };

  const methods = useForm<FeedbackFormValues>({
    mode: "onBlur",
    defaultValues: {
      name: user?.fullName,
    },
  });

  const onSubmit: SubmitHandler<FeedbackFormValues> = (data) => {
    setSendingFeedback(true);
    Sentry.captureFeedback({
      email: data.email,
      name: data.name,
      message: data.message,
      associatedEventId: eventId,
    });

    // Show toast - it will be visible during timeout
    showToast(tError("error_boundary_thank_you"), "success");

    // Wait 1s for toast to be visible, then redirect
    setTimeout(() => {
      setSendingFeedback(false);
      window.location.href = "/";
    }, 1000);
  };

  const { handleSubmit } = methods;

  return (
    <PublicLayout>
      <ContentWrapper>
        <h1 className="mb-3">{tError("error_boundary_form")}</h1>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-2 gap-4">
              <TextInput
                name="name"
                placeholder={renderPlaceholderWithAsterisk(
                  tInput("first_name_input"),
                )}
                required
                options={{
                  required: validators.required,
                }}
                disabled={sendingFeedback}
              />
              <TextInput
                name="email"
                type="email"
                placeholder={renderPlaceholderWithAsterisk(
                  tInput("email_contact_placeholder"),
                )}
                required
                options={{
                  required: validators.required,
                }}
                disabled={sendingFeedback}
              />
            </div>
            <TextAreaInput
              className="h-[150px]"
              name="message"
              placeholder={renderPlaceholderWithAsterisk(
                tError("error_boundary_explain"),
              )}
              required
              options={{
                required: validators.required,
              }}
              disabled={sendingFeedback}
            />
            <FlexContent>
              <Button
                onClick={handleGoHome}
                label={tError("error_boundary_do_not_send_report")}
                disabled={sendingFeedback}
              />
              <SubmitButton
                label={tError("error_boundary_send_report")}
                isDisabled={sendingFeedback}
              />
            </FlexContent>
          </form>
        </FormProvider>
        <p className="text-gray-dark mt-2 mb-2 text-[14px]/[24px] tracking-[-0.14px]">
          {tInput("required_fields")}
        </p>
      </ContentWrapper>
    </PublicLayout>
  );
};

export default ErrorBoundaryForm;
